if (!customElements.get('mat-coverage-calculator')) {
  customElements.define(
    'mat-coverage-calculator',
    class MatCoverageCalculator extends HTMLElement {
      static FEET_TO_METRES = 0.3048;
      static DEBOUNCE_MS = 250;

      constructor() {
        super();
        this.unit = 'm';
        this.debounceTimer = null;
        this.onSubmit = this.onSubmit.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onUnitChange = this.onUnitChange.bind(this);
        this.onResultsClick = this.onResultsClick.bind(this);
        this.onApplyQuantity = this.onApplyQuantity.bind(this);
        this.onPickerChange = this.onPickerChange.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        this.widthInput = this.querySelector('[data-mat-calc-width]');
        this.lengthInput = this.querySelector('[data-mat-calc-length]');
        this.widthLabel = this.querySelector('[data-mat-calc-width-label]');
        this.lengthLabel = this.querySelector('[data-mat-calc-length-label]');
        this.unitInputs = [...this.querySelectorAll('[data-mat-calc-unit]')];
        this.errorEl = this.querySelector('[data-mat-calc-error-text]')?.closest('.mat-calculator__error');
        this.errorText = this.querySelector('[data-mat-calc-error-text]');
        this.resultEl = this.querySelector('[data-mat-calc-result]');
        this.resultCount = this.querySelector('[data-mat-calc-result-count]');
        this.resultDetail = this.querySelector('[data-mat-calc-result-detail]');
        this.resultsList = this.querySelector('[data-mat-calc-results]');
        this.allowanceInput = this.querySelector('[data-mat-calc-allowance]');
        this.applyButton = this.querySelector('[data-mat-calc-apply]');
        this.estimatePrice = this.querySelector('[data-mat-calc-price]');
        this.productInfo = this.closest('product-info');

        if (!this.form || !this.widthInput || !this.lengthInput) return;

        this.tileWidthCm = parseFloat(this.dataset.tileWidthCm) || 100;
        this.tileLengthCm = parseFloat(this.dataset.tileLengthCm) || 100;

        this.form.addEventListener('submit', this.onSubmit);
        this.widthInput.addEventListener('input', this.onInput);
        this.lengthInput.addEventListener('input', this.onInput);
        this.unitInputs.forEach((input) => input.addEventListener('change', this.onUnitChange));
        this.resultsList?.addEventListener('click', this.onResultsClick);
        this.allowanceInput?.addEventListener('change', this.onInput);
        this.applyButton?.addEventListener('click', this.onApplyQuantity);
        this.productInfo?.addEventListener('mat-tier-picker:change', this.onPickerChange);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
        this.widthInput?.removeEventListener('input', this.onInput);
        this.lengthInput?.removeEventListener('input', this.onInput);
        this.unitInputs.forEach((input) => input.removeEventListener('change', this.onUnitChange));
        this.resultsList?.removeEventListener('click', this.onResultsClick);
        this.allowanceInput?.removeEventListener('change', this.onInput);
        this.applyButton?.removeEventListener('click', this.onApplyQuantity);
        this.productInfo?.removeEventListener('mat-tier-picker:change', this.onPickerChange);
        window.clearTimeout(this.debounceTimer);
      }

      get picker() {
        return document.getElementById(`MatTierPicker-${this.dataset.section}`);
      }

      onUnitChange(event) {
        this.unit = event.target.value;
        const unitLabel = this.unit === 'ft' ? this.dataset.stringUnitFeet : this.dataset.stringUnitMetres;
        if (this.widthLabel) this.widthLabel.textContent = this.dataset.stringWidthLabel.replace('{{ unit }}', unitLabel);
        if (this.lengthLabel) this.lengthLabel.textContent = this.dataset.stringLengthLabel.replace('{{ unit }}', unitLabel);
        this.onInput();
      }

      onInput() {
        window.clearTimeout(this.debounceTimer);
        if (this.dataset.inline === 'true') {
          this.calculatedMats = null;
          this.resultEl.hidden = true;
          this.applyButton.disabled = true;
          this.hideError();
          return;
        }
        this.debounceTimer = window.setTimeout(() => this.calculate({ announce: false }), MatCoverageCalculator.DEBOUNCE_MS);
      }

      onPickerChange() {
        if (this.dataset.inline === 'true') {
          if (this.calculatedMats) this.renderInlinePrice(this.calculatedMats);
          return;
        }
        this.onInput();
      }

      onSubmit(event) {
        event.preventDefault();
        window.clearTimeout(this.debounceTimer);
        this.calculate({ announce: true });
      }

      toMetres(value) {
        return this.unit === 'ft' ? value * MatCoverageCalculator.FEET_TO_METRES : value;
      }

      calculate({ announce }) {
        const rawWidth = parseFloat(this.widthInput.value);
        const rawLength = parseFloat(this.lengthInput.value);

        this.calculatedMats = null;
        if (!this.widthInput.validity.valid || !this.lengthInput.validity.valid ||
          !Number.isFinite(rawWidth) || !Number.isFinite(rawLength) || rawWidth <= 0 || rawLength <= 0) {
          this.resultEl.hidden = true;
          if (this.applyButton) this.applyButton.disabled = true;
          if (announce) this.showError();
          return;
        }

        this.hideError();

        const widthM = this.toMetres(rawWidth);
        const lengthM = this.toMetres(rawLength);
        const tileWM = this.tileWidthCm / 100;
        const tileLM = this.tileLengthCm / 100;
        const epsilon = 1e-6;

        const across = Math.ceil(widthM / tileWM - epsilon);
        const down = Math.ceil(lengthM / tileLM - epsilon);
        const mats = across * down;
        const area = (widthM * lengthM).toFixed(1);

        if (this.dataset.inline === 'true') {
          // Estimate by total area, allowing cut pieces to be reused around the room.
          const allowance = this.allowanceInput?.checked ? 1.1 : 1;
          const estimatedMats = Math.max(1, Math.ceil(widthM * lengthM / (tileWM * tileLM) * allowance - epsilon));
          this.calculatedMats = estimatedMats;
          this.showResult(estimatedMats, across, down, area);
          const detail = this.allowanceInput?.checked ? this.dataset.stringEstimateExtra : this.dataset.stringEstimate;
          const [before, after] = detail.split('[area]');
          const areaValue = document.createElement('strong');
          areaValue.textContent = area;
          this.resultDetail.replaceChildren(before, areaValue, after || '');
          this.renderInlinePrice(estimatedMats);
          return;
        }

        this.showResult(mats, across, down, area);
        this.renderThicknessResults(mats);
      }

      renderInlinePrice(mats) {
        const picker = this.picker;
        const thickness = picker?.getCurrentThickness();
        if (!picker?.isReady || !thickness) {
          this.applyButton.disabled = true;
          return;
        }

        const pricing = picker.getPricingFor(thickness, mats);
        this.querySelector('[data-mat-calc-thickness]').textContent = thickness;
        this.querySelector('[data-mat-calc-rate]').textContent = picker.formatMoney(pricing.perMatCents);
        this.querySelector('[data-mat-calc-total]').textContent = picker.formatMoney(pricing.totalCents);
        this.applyButton.disabled = !picker.getUnitAvailable(thickness);
      }

      onApplyQuantity() {
        window.clearTimeout(this.debounceTimer);
        if (!this.calculatedMats || this.applyButton.disabled) return;
        this.picker.selectQuantity(this.calculatedMats);
        this.closest('details')?.removeAttribute('open');
      }

      showError() {
        if (!this.errorEl) return;
        this.errorText.textContent = this.dataset.stringErrorInvalid;
        this.errorEl.hidden = false;
        this.resultEl.hidden = true;
        [this.widthInput, this.lengthInput].forEach((input) => {
          if (!input.validity.valid || !input.value || parseFloat(input.value) <= 0) input.setAttribute('aria-invalid', 'true');
        });
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
        this.widthInput.removeAttribute('aria-invalid');
        this.lengthInput.removeAttribute('aria-invalid');
      }

      showResult(mats, across, down, area) {
        const template = mats === 1 ? this.dataset.stringResultSingle : this.dataset.stringResultMultiple;
        this.resultCount.textContent = template.replace('[count]', mats);
        this.resultDetail.textContent = this.dataset.stringResultDetail
          .replace('[across]', across)
          .replace('[down]', down)
          .replace('[area]', area);
        this.resultEl.hidden = false;
      }

      renderThicknessResults(mats) {
        if (!this.resultsList) return;
        this.resultsList.innerHTML = '';

        const picker = this.picker;
        if (!picker?.isReady) return;

        for (const thickness of picker.getThicknesses()) {
          const item = document.createElement('li');
          item.className = 'mat-calculator__option';
          item.dataset.thickness = thickness;

          const info = document.createElement('div');
          info.className = 'mat-calculator__option-info';

          const name = document.createElement('span');
          name.className = 'mat-calculator__option-name';
          name.textContent = thickness;
          info.append(name);

          const detail = document.createElement('span');
          detail.className = 'mat-calculator__option-detail';

          let total = null;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'button button--primary mat-calculator__option-button';
          button.textContent = this.dataset.stringAddToCart;

          if (!picker.getUnitAvailable(thickness)) {
            detail.textContent = picker.dataset.stringSoldOut;
            button.disabled = true;
          } else {
            const pricing = picker.getPricingFor(thickness, mats);
            detail.innerHTML = '';
            const count = document.createElement('strong');
            count.className = 'mat-calculator__option-count';
            count.textContent = this.dataset.stringResultRowCount.replace('[count]', mats);
            const price = document.createElement('span');
            price.className = 'mat-calculator__option-price';
            price.textContent = this.dataset.stringResultRowPrice
              .replace('[price]', picker.formatMoney(pricing.perMatCents))
              .replace('[total]', picker.formatMoney(pricing.totalCents));
            detail.append(count, price);

            total = document.createElement('span');
            total.className = 'mat-calculator__option-total';
            total.textContent = picker.formatMoney(pricing.totalCents);

            button.dataset.thickness = thickness;
            button.dataset.quantity = String(mats);
          }

          info.append(detail);
          const action = document.createElement('div');
          action.className = 'mat-calculator__option-action';
          if (total) action.append(total);
          action.append(button);
          item.append(info, action);
          this.resultsList.append(item);
        }
      }

      onResultsClick(event) {
        const button = event.target.closest('.mat-calculator__option-button');
        if (!button || button.disabled || !button.dataset.thickness) return;

        const picker = this.picker;
        if (!picker) return;

        const thickness = button.dataset.thickness;
        const quantity = parseInt(button.dataset.quantity, 10);

        button.disabled = true;
        const originalLabel = button.textContent;
        button.textContent = this.dataset.stringAdding;

        const variantId = picker.addToCartForThickness(thickness, quantity);

        window.setTimeout(() => {
          button.textContent = variantId ? this.dataset.stringAdded : originalLabel;
          window.setTimeout(() => {
            button.textContent = originalLabel;
            button.disabled = false;
          }, 1500);
        }, 400);
      }
    }
  );
}
